import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

const FOCUSABLE_QUERY = `a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])`;

function isElementEnabled(element: Element): boolean {
  if (element.hasAttribute('disabled')) {
    return false;
  } else if (element.ariaDisabled === 'true') {
    return false;
  } else {
    return true;
  }
}

function focusElement(element: Element | undefined) {
  if (element instanceof HTMLElement) {
    element.focus();
  }
}

export function focusWithin(element: Element) {
  if (!(element instanceof HTMLElement)) return;

  const allFocusableElements = Array.from(
    element.querySelectorAll(FOCUSABLE_QUERY)
  ).filter(isElementEnabled);

  focusElement(allFocusableElements[0]);
}

export interface UseModalAutoFocusOptions {
  onOpenAutoFocus?: (event: Event) => void;
  onCloseAutoFocus?: (event: Event) => void;
}

export function useModalAutoFocus(
  trigger: string | ReactNode | undefined,
  options?: UseModalAutoFocusOptions
) {
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);

  const onCloseAutoFocus = useCallback(
    (event: Event) => {
      options?.onCloseAutoFocus?.(event);

      if (!event.defaultPrevented) {
        event.preventDefault();

        if (!trigger) {
          lastFocusedElementRef.current?.focus();
        } else if (triggerRef.current) {
          triggerRef.current.focus();
        } else if (typeof trigger === 'string') {
          const element = document.querySelector(trigger);
          if (element instanceof HTMLElement) {
            element.focus();
          }
        }

        // https://github.com/radix-ui/primitives/issues/1241#issuecomment-2589438039
        document.body.style.pointerEvents = '';
      }

      lastFocusedElementRef.current = null;
    },
    [trigger, options?.onCloseAutoFocus]
  );

  const onOpenAutoFocus = useCallback(
    (event: Event) => {
      if (document.activeElement instanceof HTMLElement) {
        lastFocusedElementRef.current = document.activeElement;
      }

      options?.onOpenAutoFocus?.(event);

      if (event.defaultPrevented) return;
      const content = event.target;
      if (!(content instanceof HTMLElement)) return;

      const allFocusableElements = Array.from(
        content.querySelectorAll(FOCUSABLE_QUERY)
      ).filter(isElementEnabled);

      if (!allFocusableElements.length) return;

      const closeButtonIndex = allFocusableElements.findIndex(
        (el) => el instanceof HTMLElement && el.dataset.id === 'dialog-close'
      );

      if (closeButtonIndex === 0 && allFocusableElements.length > 1) {
        focusElement(allFocusableElements[1]);
      } else {
        focusElement(allFocusableElements[0]);
      }

      event.preventDefault();
    },
    [options?.onOpenAutoFocus]
  );

  const props = useMemo(
    () => ({ onCloseAutoFocus, onOpenAutoFocus }),
    [onCloseAutoFocus, onOpenAutoFocus]
  );

  return [triggerRef, props] as const;
}

export interface ModalPartsContextValue {
  onOpenAutoFocus(event: Event): void;
  onCloseAutoFocus(event: Event): void;
  onEscapeKeyDown?(event: KeyboardEvent): void;
  onInteractOutside?(event: Event): void;
}

export const ModalPartsContext = createContext<ModalPartsContextValue | null>(
  null
);

/** The consumer's handler runs first so `preventDefault` can opt out of the
 * primitive's own focus and dismissal behaviour. */
export function composeModalHandlers<E>(
  ours: ((event: E) => void) | undefined,
  theirs: ((event: E) => void) | undefined
) {
  return (event: E) => {
    theirs?.(event);
    ours?.(event);
  };
}

export function useModalPartsContext() {
  const context = useContext(ModalPartsContext);

  if (!context) {
    throw new Error('Modal parts must be rendered inside a Root component');
  }

  return context;
}
